import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NotificationBellIcon, NotificationModal } from '@/components/NotificationBell';
import { AccessMenuItems } from '@/components/AccessMenuItems';
import { AccessShortcutButton } from '@/components/AccessShortcutButton';
import { BarChart3, Bell, BookOpenText, Building2, ChevronDown, HelpCircle, Link, LogOut, MapPin, Menu, Navigation, Settings, Shield, User, Users, UsersRound, Rocket } from 'lucide-react';

const tabClass = (active, extra = '') => `flex min-w-[66px] flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 text-[10px] sm:text-sm font-medium whitespace-nowrap rounded-xl sm:rounded-full transition-[background-color,color,box-shadow] duration-200 ${active ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'} ${extra}`;

const DropdownNav = ({
  label,
  icon,
  testId,
  itemTestPrefix,
  items,
  active,
  activeSection,
  onSelect,
  open,
  setOpen,
  onOpen,
  width = 'w-56',
}) => {
  const [pinnedClosed, setPinnedClosed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutsideClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open, setOpen]);

  const openMenu = () => {
    if (pinnedClosed) return;
    onOpen();
  };

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      setPinnedClosed(true);
      return;
    }
    setPinnedClosed(false);
    onOpen();
  };

  const handleSelect = (key) => {
    onSelect(key);
    setOpen(false);
    setPinnedClosed(false);
  };

  return (
    <div className="relative" ref={ref} onMouseEnter={openMenu} onMouseLeave={() => setPinnedClosed(false)}>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
        className={tabClass(active)}
        onClick={handleToggle}
      >
        {icon}
        {label}
        <ChevronDown className={`hidden sm:block w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" data-testid={`${testId}-menu`} className={`absolute left-0 top-full z-[70] mt-2 ${width} rounded-xl border border-slate-200 bg-white p-2 text-slate-700 shadow-xl animate-in fade-in-0 zoom-in-95 duration-150`}>
          {items.map(item => (
            <button
              type="button"
              key={item.key}
              onClick={() => handleSelect(item.key)}
              data-testid={`${itemTestPrefix}-${item.key}`}
              className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${activeSection === item.key ? 'bg-blue-600 text-white font-semibold' : 'text-slate-700 hover:bg-slate-100'}`}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.badge && <span data-testid={`${itemTestPrefix}-${item.key}-badge`} className={`ml-auto px-1.5 py-0.5 text-[10px] leading-none font-bold rounded-full ${activeSection === item.key ? 'bg-white text-blue-700' : 'bg-red-500 text-white'}`}>{item.badge}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminDashboardHeader = ({
  settings,
  user,
  activeSection,
  topNavItems,
  engagementNavItems,
  managementNavItems,
  ebiNavItems,
  showGroupChurchesTab,
  notifications,
  bellOpen,
  setBellOpen,
  fetchNotifications,
  navigate,
  openSettings,
  setSettingsTab,
  setSettingsDialog,
  setOnboardingOpen,
  setAuditoriaDialog,
  setChangelogDialog,
  handleLogout,
  selectSection,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const engagementSectionActive = engagementNavItems.some(item => item.key === activeSection);
  const managementSectionActive = managementNavItems.some(item => item.key === activeSection || item.children?.some(child => child.key === activeSection));
  const ebiSectionActive = settings?.addons?.ebi && activeSection.startsWith('ebi-');
  const movimentosSectionActive = settings?.addons?.movimentos && activeSection === 'movimentos';

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"><Users className="w-6 h-6 text-white" /></div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="admin-title">{settings.app_name}</h1>
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {(settings.city || settings.state) && <Badge variant="outline" className="text-xs"><MapPin className="w-3 h-3 mr-1" />{[settings.city, settings.state].filter(Boolean).join(' - ')}</Badge>}
                <Badge variant="outline" className="text-xs"><User className="w-3 h-3 mr-1" />{user.name}</Badge>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AccessShortcutButton navigate={navigate} testId="admin-access-shortcut-button" />
            <Button onClick={() => { setSettingsTab('links'); setSettingsDialog(true); }} variant="outline" size="icon" className="hidden sm:flex" data-testid="copy-linktree-btn" title="Configurações de Links"><Link className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => setOnboardingOpen(true)} title="Tutorial" data-testid="btn-help" className="hidden sm:inline-flex">
              <HelpCircle className="w-5 h-5 text-slate-500" />
            </Button>
            <div className="relative hidden sm:block">
              <NotificationBellIcon notifications={notifications} onClick={() => setBellOpen(true)} />
            </div>
            <NotificationModal open={bellOpen} onOpenChange={setBellOpen} notifications={notifications} onRefresh={fetchNotifications} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-2" data-testid="admin-menu-btn">
                  <Menu className="w-5 h-5" />
                  <span className="hidden sm:inline">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <AccessMenuItems user={user} currentKey="admin" navigate={navigate} />
                <DropdownMenuItem onClick={openSettings} className="cursor-pointer" data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />Configurações
                </DropdownMenuItem>
                <div className="sm:hidden">
                  <DropdownMenuItem onClick={() => { setSettingsTab('links'); setSettingsDialog(true); }} className="cursor-pointer" data-testid="menu-links-mobile">
                    <Link className="w-4 h-4 mr-2" />Links
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setOnboardingOpen(true)} className="cursor-pointer" data-testid="menu-tutorial-mobile">
                    <HelpCircle className="w-4 h-4 mr-2" />Tutorial
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBellOpen(true)} className="cursor-pointer" data-testid="menu-notifications-mobile">
                    <Bell className="w-4 h-4 mr-2" />Notificações
                  </DropdownMenuItem>
                </div>
                <DropdownMenuItem onClick={() => setAuditoriaDialog(true)} className="cursor-pointer" data-testid="menu-auditoria">
                  <Shield className="w-4 h-4 mr-2" />Auditoria
                </DropdownMenuItem>
                {user.role === 'gestor' && (
                  <DropdownMenuItem onClick={() => setChangelogDialog(true)} className="cursor-pointer" data-testid="menu-changelog">
                    <Rocket className="w-4 h-4 mr-2" />Atualizações
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-t border-slate-100">
        <div className="flex items-center gap-1 py-2 overflow-visible" data-testid="admin-tabs">
          {topNavItems.map(item => (
            <button key={item.key} onClick={() => selectSection(item.key)} data-testid={`tab-${item.key}`} className={tabClass(activeSection === item.key)}>
              {item.icon}
              {item.label}
              {item.badge && <span data-testid={`tab-${item.key}-badge`} className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] leading-none font-bold rounded-full">{item.badge}</span>}
            </button>
          ))}

          <DropdownNav label="Engajamento" icon={<UsersRound className="w-3.5 h-3.5" />} testId="tab-engajamento-dropdown" itemTestPrefix="tab-engajamento" items={engagementNavItems} active={engagementSectionActive} activeSection={activeSection} onSelect={selectSection} open={openDropdown === 'engajamento'} setOpen={(value) => setOpenDropdown(value ? 'engajamento' : null)} onOpen={() => setOpenDropdown('engajamento')} width="w-60" />
          <DropdownNav label="Gestão" icon={<Settings className="w-3.5 h-3.5" />} testId="tab-gestao-dropdown" itemTestPrefix="tab-gestao" items={managementNavItems} active={managementSectionActive} activeSection={activeSection} onSelect={selectSection} open={openDropdown === 'gestao'} setOpen={(value) => setOpenDropdown(value ? 'gestao' : null)} onOpen={() => setOpenDropdown('gestao')} />

          {settings?.addons?.ebi && (
            <button onClick={() => selectSection('ebi-dashboard')} data-testid="tab-ebi" className={tabClass(ebiSectionActive)}>
              <BookOpenText className="w-3.5 h-3.5" />
              EBI
            </button>
          )}

          {settings?.addons?.movimentos && (
            <button type="button" onClick={() => selectSection('movimentos')} data-testid="tab-movimentos" className={tabClass(movimentosSectionActive)}>
              <Navigation className="w-3.5 h-3.5" />
              Movimentos
            </button>
          )}

          <button onClick={() => selectSection('reports')} data-testid="tab-reports" className={tabClass(activeSection === 'reports')}>
            <BarChart3 className="w-3.5 h-3.5" />
            Relatórios
          </button>
          {showGroupChurchesTab && (
            <button onClick={() => selectSection('igrejas-grupo')} data-testid="tab-igrejas-grupo" className={tabClass(activeSection === 'igrejas-grupo', 'ml-auto min-w-[74px]')}>
              <Building2 className="w-3.5 h-3.5" />
              <span className="sm:hidden">Grupo</span><span className="hidden sm:inline">Igrejas do Grupo</span>
            </button>
          )}
        </div>
      </nav>
    </header>
  );
};